import { motion } from "framer-motion";
import { Zap, Layers, Sparkles, ShieldCheck } from "lucide-react";

const ITEMS = [
  {
    icon: Zap,
    title: "Performance-first",
    desc: "Optimized rendering pipelines, GPU profiling and aggressive memory budgets baked into every build.",
  },
  {
    icon: Layers,
    title: "Custom-tailored stack",
    desc: "We pick the right engine, framework and architecture for your product — never a one-size template.",
  },
  {
    icon: Sparkles,
    title: "Immersion as a craft",
    desc: "Spatial audio, haptics, motion design and lighting tuned for true presence and emotional impact.",
  },
  {
    icon: ShieldCheck,
    title: "Engineering excellence",
    desc: "Strong typing, CI, automated tests and code reviews. Your 3D product behaves like a serious product.",
  },
];

export default function Differentials() {
  return (
    <section
      id="differentials"
      data-testid="differentials-section"
      className="relative py-24 sm:py-32 px-5 sm:px-8"
    >
      <div className="absolute inset-0 bg-tech-grid opacity-50 pointer-events-none" />
      <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 h-[420px] w-[700px] rounded-full bg-[#00FF66]/10 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B00]">
            Why AnimaSoftware
          </div>
          <h2
            className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter"
            data-testid="differentials-headline"
          >
            Built different. <span className="text-gradient">By design.</span>
          </h2>
          <p className="mt-6 text-gray-400 text-base sm:text-lg max-w-2xl">
            Four reasons teams hire us to architect their next 3D product and
            stay with us long after launch.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.1 } },
          }}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden"
        >
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            const isOrange = i % 2 === 0;
            return (
              <motion.div
                key={it.title}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
                className="group bg-[#0A0A0A] p-7 sm:p-8 hover:bg-white/[0.03] transition-all duration-300"
                data-testid={`differential-${i + 1}`}
              >
                <div
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 mb-6 ${
                    isOrange
                      ? "bg-[#FF6B00]/10 text-[#FF6B00]"
                      : "bg-[#00FF66]/10 text-[#00FF66]"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-[Unbounded] text-xl font-semibold mb-3">
                  {it.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {it.desc}
                </p>
                <div
                  className={`mt-7 h-px w-10 ${
                    isOrange ? "bg-[#FF6B00]" : "bg-[#00FF66]"
                  } group-hover:w-20 transition-all duration-500`}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
